import { MessagesSquare } from 'lucide-react'
import { Avatar } from '@/components/design-system'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { PanelEmptyState } from '@/frontend/components/EmptyState'
import { channelLabel, minutesAgoLabel } from './format'
import type { ActiveConversation, WorkerAvatarMap } from './types'

interface Props {
  conversations: ActiveConversation[]
  workerAvatars?: WorkerAvatarMap
}

/**
 * "Conversas ativas · últimos 15 min" (Iteração 3, Onda A): as sessões vivas
 * da Fleet, listadas INDIVIDUALMENTE como conversas — avatar do agente + nome
 * + assunto/contato + canal + "há X min", com o dot pulsante ao vivo. Dado
 * real (fleet_sessions com last_event_at recente). Sem conversa, some.
 */
export function ActiveConversations({ conversations, workerAvatars = {} }: Props) {
  // Coluna fixa (par de "Publicações recentes"): vazia mostra o estado em vez
  // de sumir, pra o grid de duas colunas do rodapé não colapsar.
  if (conversations.length === 0) {
    return (
      <section aria-label="Conversas ativas" className="space-y-2">
        <SectionLabel>Conversas ativas</SectionLabel>
        <PanelEmptyState icon={MessagesSquare} title="Tudo quieto por aqui" description="Ninguém batendo papo agora." />
      </section>
    )
  }

  return (
    <section aria-label="Conversas ativas" className="space-y-2">
      <SectionLabel>
        Conversas ativas
        <span className="rounded-full bg-secondary px-[7px] text-[10.5px] font-semibold text-muted-foreground">
          {conversations.length}
        </span>
        <span className="font-normal normal-case tracking-normal text-faint-foreground">· últimos 15 min</span>
      </SectionLabel>

      <ul className="overflow-hidden rounded-xl border border-border bg-card">
        {conversations.map((conversation) => {
          const channel = channelLabel(conversation.channel)
          const meta = [conversation.subject, channel].filter(Boolean).join(' · ')
          return (
            <li
              key={conversation.id}
              className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
            >
              <span
                className="h-[7px] w-[7px] flex-none animate-live-pulse rounded-full bg-success"
                aria-hidden
              />
              <Avatar src={workerAvatars[conversation.worker_id] ?? undefined} alt="" name={conversation.worker_name} hueSeed={conversation.worker_id} size="xxs" className="flex-none" />
              <p className="min-w-0 flex-1 truncate text-[13px]">
                <b className="font-semibold text-foreground">{conversation.worker_name}</b>
                {meta && <span className="text-muted-foreground"> · {meta}</span>}
              </p>
              <span className="flex-none font-mono text-[11px] text-faint-foreground">
                {minutesAgoLabel(conversation.last_event_at)}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
